/**
 * ComplianceChecklist Component
 * Displays permit requirements with upload status for each item
 *
 * Part of Sprint Day 5-6: Compliance Dashboard UI
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Upload,
  FileText,
  Download,
  ChevronDown,
  ChevronRight,
  Loader2,
  Info,
  Sparkles,
  HelpCircle,
} from "lucide-react";
import { useRequiredChecklist, type ChecklistItem } from "@/hooks/useCompliance";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CommunityTips } from "@/components/inspection/CommunityTips";

interface JobDocument {
  id: string;
  category: string;
  fileName: string;
  status?: 'valid' | 'invalid' | 'needs_review' | 'uploaded';
  extractedData?: Record<string, any>;
}

interface ComplianceChecklistProps {
  jurisdictionId: number | null;
  permitType: string;
  jobScope?: string;
  jobDocuments: JobDocument[];
  onUploadClick: (category: string, requirement?: ChecklistItem) => void;
  onFillClick?: (formId: string, formName: string) => void;
  loading?: boolean;
}

export function ComplianceChecklist({
  jurisdictionId,
  permitType,
  jobScope,
  jobDocuments,
  onUploadClick,
  onFillClick,
  loading: externalLoading
}: ComplianceChecklistProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['critical'])
  );

  const { data: checklistData, isLoading, error } = useRequiredChecklist({
    jurisdictionId,
    permitType,
    jobScope
  });

  if (isLoading || externalLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading requirements...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!jurisdictionId || !permitType) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Select a jurisdiction and permit type to see requirements</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !checklistData?.success) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p>Failed to load requirements checklist</p>
            <p className="text-sm mt-1">
              {(error as Error)?.message || 'Please try again'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { checklist, totalRequired, totalOptional } = checklistData;

  // Match documents to requirements
  const checklistWithStatus = checklist.map(item => {
    const matchingDoc = jobDocuments.find(doc =>
      doc.category?.toLowerCase() === item.category?.toLowerCase()
    );
    return {
      ...item,
      document: matchingDoc,
      uploadStatus: matchingDoc
        ? (matchingDoc.status || 'uploaded')
        : 'missing'
    } as ChecklistItemWithStatus;
  });

  // Calculate progress
  const uploadedCount = checklistWithStatus.filter(
    item => item.uploadStatus !== 'missing' && item.isRequired
  ).length;
  const requiredCount = checklistWithStatus.filter(item => item.isRequired).length;
  const progressPercent = requiredCount > 0 ? (uploadedCount / requiredCount) * 100 : 0;

  // Group by severity
  const criticalItems = checklistWithStatus.filter(item => item.severity === 'critical');
  const warningItems = checklistWithStatus.filter(item => item.severity === 'warning');
  const infoItems = checklistWithStatus.filter(item => item.severity === 'info');

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Requirements Checklist
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {uploadedCount} / {requiredCount} Required
            </Badge>
            {totalOptional > 0 && (
              <Badge variant="secondary" className="text-xs">
                +{totalOptional} Optional
              </Badge>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-muted-foreground text-right">
            {Math.round(progressPercent)}% complete
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* HVHZ Notice if applicable */}
        {checklistData.jurisdiction?.isHvhz && (
          <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                High Velocity Hurricane Zone (HVHZ)
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">
                This jurisdiction requires enhanced documentation including Florida Product Approvals
                (NOA/FBC) and additional wind load calculations.
              </p>
            </div>
          </div>
        )}

        {/* Community Warnings/Tips */}
        {jurisdictionId && (
          <CommunityTips
            jurisdictionId={jurisdictionId}
            context="compliance"
            defaultExpanded={false}
            limit={5}
          />
        )}

        {/* Critical Requirements - Split into incomplete (red box) and complete (green) */}
        {criticalItems.length > 0 && (
          <ChecklistCategorySplit
            title="Critical Requirements"
            items={criticalItems}
            severity="critical"
            expanded={expandedCategories.has('critical')}
            onToggle={() => toggleCategory('critical')}
            onUploadClick={onUploadClick}
            onFillClick={onFillClick}
          />
        )}

        {/* Warning Requirements */}
        {warningItems.length > 0 && (
          <ChecklistCategorySplit
            title="Important Documents"
            items={warningItems}
            severity="warning"
            expanded={expandedCategories.has('warning')}
            onToggle={() => toggleCategory('warning')}
            onUploadClick={onUploadClick}
            onFillClick={onFillClick}
          />
        )}

        {/* Info/Optional */}
        {infoItems.length > 0 && (
          <ChecklistCategorySplit
            title="Optional Documents"
            items={infoItems}
            severity="info"
            expanded={expandedCategories.has('info')}
            onToggle={() => toggleCategory('info')}
            onUploadClick={onUploadClick}
            onFillClick={onFillClick}
          />
        )}

        {/* Empty state */}
        {checklist.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No specific requirements found for this permit type.</p>
            <p className="text-sm">Standard documentation may still be required.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Extended type for checklist items with status
type ChecklistItemWithStatus = ChecklistItem & {
  document?: JobDocument;
  uploadStatus: 'valid' | 'invalid' | 'needs_review' | 'uploaded' | 'missing';
};

interface ChecklistCategoryProps {
  title: string;
  items: ChecklistItemWithStatus[];
  severity: 'critical' | 'warning' | 'info';
  expanded: boolean;
  onToggle: () => void;
  onUploadClick: (category: string, requirement?: ChecklistItem) => void;
  onFillClick?: (formId: string, formName: string) => void;
}

/**
 * ChecklistCategorySplit - Separates completed items from incomplete ones
 * Incomplete items appear in a colored box, completed items appear below in a neutral/green style
 */
function ChecklistCategorySplit({
  title,
  items,
  severity,
  expanded,
  onToggle,
  onUploadClick,
  onFillClick
}: ChecklistCategoryProps) {
  // Split items into complete and incomplete
  const incompleteItems = items.filter(i => i.uploadStatus === 'missing' || i.uploadStatus === 'invalid' || i.uploadStatus === 'needs_review');
  const completeItems = items.filter(i => i.uploadStatus === 'valid' || i.uploadStatus === 'uploaded');

  const missingCount = items.filter(i => i.uploadStatus === 'missing').length;
  const invalidCount = items.filter(i => i.uploadStatus === 'invalid').length;

  const severityStyles = {
    critical: {
      border: 'border-red-200 dark:border-red-800',
      bg: 'bg-red-50 dark:bg-red-950/50',
      headerBg: 'hover:bg-red-100 dark:hover:bg-red-900/50',
      badge: 'destructive' as const
    },
    warning: {
      border: 'border-yellow-200 dark:border-yellow-800',
      bg: 'bg-yellow-50 dark:bg-yellow-950/50',
      headerBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/50',
      badge: 'secondary' as const
    },
    info: {
      border: 'border-blue-200 dark:border-blue-800',
      bg: 'bg-blue-50 dark:bg-blue-950/50',
      headerBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/50',
      badge: 'outline' as const
    }
  };

  const styles = severityStyles[severity];
  const allComplete = incompleteItems.length === 0;

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-2 text-left transition-colors hover:bg-muted/50 rounded-lg"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span className="font-medium">{title}</span>
          <Badge variant={allComplete ? "outline" : styles.badge}>
            {items.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {invalidCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {invalidCount} invalid
            </Badge>
          )}
          {missingCount > 0 && (
            <Badge variant="outline" className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-700">
              {missingCount} missing
            </Badge>
          )}
          {allComplete && (
            <Badge variant="outline" className="text-green-600 dark:text-green-400 border-green-300 dark:border-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Complete
            </Badge>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="space-y-2 ml-6">
          {/* Incomplete items in colored box */}
          {incompleteItems.length > 0 && (
            <div className={cn("border rounded-lg overflow-hidden", styles.border, styles.bg)}>
              <div className="p-2 space-y-1">
                {incompleteItems.map((item, index) => (
                  <ChecklistItemRow
                    key={item.id || `incomplete-${index}`}
                    item={item}
                    onUploadClick={() => onUploadClick(item.category, item)}
                    onFillClick={onFillClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Complete items in green/neutral box */}
          {completeItems.length > 0 && (
            <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 rounded-lg overflow-hidden">
              <div className="p-2 space-y-1">
                {completeItems.map((item, index) => (
                  <ChecklistItemRow
                    key={item.id || `complete-${index}`}
                    item={item}
                    onUploadClick={() => onUploadClick(item.category, item)}
                    onFillClick={onFillClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ChecklistItemRowProps {
  item: ChecklistItemWithStatus;
  onUploadClick: () => void;
  onFillClick?: (formId: string, formName: string) => void;
}

function ChecklistItemRow({ item, onUploadClick, onFillClick }: ChecklistItemRowProps) {
  const statusConfig = {
    valid: {
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      text: 'Verified'
    },
    uploaded: {
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      text: 'Uploaded'
    },
    invalid: {
      icon: <XCircle className="h-4 w-4 text-red-600" />,
      text: 'Invalid'
    },
    needs_review: {
      icon: <AlertCircle className="h-4 w-4 text-yellow-600" />,
      text: 'Needs Review'
    },
    missing: {
      icon: <XCircle className="h-4 w-4 text-gray-400" />,
      text: 'Missing'
    }
  };

  const status = statusConfig[item.uploadStatus];
  const matchingForm = item.matchingCustomForms?.[0]; // Use first matching form

  return (
    <div className="flex items-center justify-between p-2 rounded">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {status.icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{item.name}</p>
            {item.isRequired && (
              <Badge variant="outline" className="text-xs shrink-0">
                Required
              </Badge>
            )}
            {item.isFillable && item.hasMatchingForm && (
              <Badge variant="secondary" className="text-xs shrink-0 bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                <Sparkles className="h-2.5 w-2.5 mr-1" />
                Auto-fill
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-muted-foreground truncate">
              {item.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 ml-2">
        {/* Help tooltip for upload-only items */}
        {!item.isFillable && item.uploadHelpText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <HelpCircle className="h-3.5 w-3.5 text-blue-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs font-medium mb-1">How to get this document:</p>
                <p className="text-xs">{item.uploadHelpText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Info tooltip */}
        {(item.instructions || item.commonMistakes) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {item.instructions && (
                  <p className="text-xs mb-1">{item.instructions}</p>
                )}
                {item.commonMistakes && (
                  <p className="text-xs text-yellow-600">
                    Common mistakes: {item.commonMistakes}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Download template button */}
        {item.templateUrl && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild>
                  <a href={item.templateUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download template</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Action buttons based on status and fillable state */}
        {item.uploadStatus === 'missing' ? (
          <div className="flex items-center gap-1">
            {/* Fill button for fillable items with matching form */}
            {item.isFillable && item.hasMatchingForm && matchingForm && onFillClick && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      type="button"
                      variant="default"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onFillClick(matchingForm.id, matchingForm.name);
                      }}
                      className="h-8 z-10 relative bg-purple-600 hover:bg-purple-700"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      Fill
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Auto-fill using: {matchingForm.name}</p>
                    <p className="text-xs text-muted-foreground">{matchingForm.fieldCount} fields</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Upload button */}
            <Button
              size="sm"
              type="button"
              variant={item.isFillable && item.hasMatchingForm ? "outline" : "default"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onUploadClick();
              }}
              className="h-8 z-10 relative"
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              Upload
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground truncate max-w-[100px]">
            {item.document?.fileName || status.text}
          </span>
        )}
      </div>
    </div>
  );
}

export default ComplianceChecklist;
